/**
 * Debug utility for Document Analysis page issues
 * This provides comprehensive logging and diagnostics for redirect problems
 */

export const debugDocumentAnalysis = {
  /**
   * Log comprehensive debug information about the current state
   */
  logState: (state) => {
    console.group('🔍 Document Analysis Debug');
    
    console.log('📋 Basic Info:', {
      loanId: state.loanId,
      hasLoanId: !!state.loanId,
      timestamp: new Date().toISOString()
    });
    
    console.log('📊 Loading States:', {
      isLoadingFromDatabase: state.isLoadingFromDatabase,
      isActive: state.isActive,
      isAnimating: state.isAnimating
    });
    
    console.log('📁 File States:', {
      hasProposalFile: !!state.proposalFile,
      hasNoteFile: !!state.noteFile,
      hasSecurityFile: !!state.securityFile,
      hasFiles: !!(state.proposalFile || state.noteFile || state.securityFile)
    });
    
    console.log('📈 Results States:', {
      hasExtractionResults: !!state.extractionResults,
      hasAnalysisResults: !!state.analysisResults,
      extractionResultsType: typeof state.extractionResults,
      analysisResultsType: typeof state.analysisResults
    });
    
    if (state.extractionResults) {
      console.log('📋 Extraction Results Details:', {
        keys: Object.keys(state.extractionResults),
        keyCount: Object.keys(state.extractionResults).length,
        hasMetadata: !!state.extractionResults._metadata,
        metadataLoanId: state.extractionResults._metadata?.loan_id,
        directLoanId: state.extractionResults.loan_id,
        hasMatchResult: !!state.extractionResults.match_result,
        hasExtractionResult: !!state.extractionResults.extraction_result,
        fieldKeys: Object.keys(state.extractionResults).filter(k => k !== '_metadata'),
        fieldCount: Object.keys(state.extractionResults).filter(k => k !== '_metadata').length
      });
      
      // Show sample field data
      const sampleFields = Object.keys(state.extractionResults)
        .filter(k => k !== '_metadata')
        .slice(0, 3);
      if (sampleFields.length > 0) {
        console.log('📋 Sample Field Data:', sampleFields.reduce((acc, key) => {
          acc[key] = {
            value: state.extractionResults[key].value,
            source: state.extractionResults[key].source,
            page: state.extractionResults[key].page
          };
          return acc;
        }, {}));
      }
    }
    
    if (state.analysisResults) {
      console.log('📋 Analysis Results Details:', {
        keys: Object.keys(state.analysisResults),
        keyCount: Object.keys(state.analysisResults).length
      });
    }
    
    console.groupEnd();
  },
  
  /**
   * Validate that the hasResults logic is working correctly
   */
  validateHasResults: (state) => {
    const { extractionResults, analysisResults, loanId } = state;
    
    console.group('✅ HasResults Validation');
    
    let hasResults = false;
    let reason = 'No data found';
    
    // Check extraction results
    if (extractionResults) {
      const resultsLoanId = extractionResults._metadata?.loan_id || extractionResults.loan_id;
      
      if (!resultsLoanId || resultsLoanId === loanId) {
        const hasMatchResult = extractionResults.match_result && Object.keys(extractionResults.match_result).length > 0;
        const hasExtractionResult = extractionResults.extraction_result && Object.keys(extractionResults.extraction_result).length > 0;
        const fieldKeys = Object.keys(extractionResults).filter(key => key !== '_metadata');
        const hasDirectData = !extractionResults.match_result && !extractionResults.extraction_result && fieldKeys.length > 0;
        
        if (hasMatchResult) {
          hasResults = true;
          reason = 'Has match_result data';
        } else if (hasExtractionResult) {
          hasResults = true;
          reason = 'Has extraction_result data';
        } else if (hasDirectData) {
          hasResults = true;
          reason = `Has ${fieldKeys.length} field data entries`;
        }
      } else {
        reason = `Loan ID mismatch: current=${loanId}, results=${resultsLoanId}`;
      }
    }
    
    // Check analysis results
    if (!hasResults && analysisResults && Object.keys(analysisResults).length > 0) {
      hasResults = true;
      reason = 'Has analysis results';
    }
    
    console.log('Result:', { hasResults, reason });
    
    if (!hasResults && loanId) {
      console.warn('⚠️ WARNING: Should redirect to /scans');
    }
    
    console.groupEnd();
    
    return { hasResults, reason };
  },
  
  /**
   * Check if redirect conditions are met
   */
  shouldRedirect: (state) => {
    const { loanId, hasResults, isLoadingFromDatabase, isActive, isAnimating } = state;
    
    const shouldRedirect = loanId && !hasResults && !isLoadingFromDatabase && !isActive && !isAnimating;
    
    console.log('🚀 Redirect Check:', {
      loanId: !!loanId,
      hasResults,
      isLoadingFromDatabase,
      isActive,
      isAnimating,
      shouldRedirect
    });
    
    return shouldRedirect;
  },
  
  /**
   * Complete diagnostic for current state
   */
  diagnose: (state) => {
    console.group('🏥 Document Analysis Diagnosis');
    
    debugDocumentAnalysis.logState(state);
    const validation = debugDocumentAnalysis.validateHasResults(state);
    const redirect = debugDocumentAnalysis.shouldRedirect(state);
    
    console.log('📋 Summary:', {
      hasResults: validation.hasResults,
      reason: validation.reason,
      shouldRedirect: redirect,
      recommendation: redirect ? 'Redirect to /scans' : 'Show results'
    });
    
    console.groupEnd();
    
    return {
      hasResults: validation.hasResults,
      shouldRedirect: redirect,
      recommendation: redirect ? 'Redirect to /scans' : 'Show results'
    };
  }
};

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.debugDocumentAnalysis = debugDocumentAnalysis;
}
